import { el } from '../utils/dom.js';
import { PixelLabClient } from './PixelLabClient.js';

/**
 * PixelLab integration — generate tilesets via AI from text descriptions.
 * Users supply their own PixelLab API key (free tier available).
 */

const STORAGE_KEY = 'pixellab_api_key';

export class PixelLabPanel {
  constructor(state, mapCanvas, compositor) {
    this._state = state;
    this._mapCanvas = mapCanvas;
    this._compositor = compositor;
    this._dialog = null;
    this._secureStorage = null;
    this._cachedApiKey = localStorage.getItem(STORAGE_KEY) || '';
  }

  /** Bind SecureStorage for encrypted API key storage. */
  setSecureStorage(secureStorage) {
    this._secureStorage = secureStorage;
    secureStorage.get(STORAGE_KEY).then(val => {
      if (val) this._cachedApiKey = val;
    }).catch(() => {});
  }

  _saveApiKey(key) {
    this._cachedApiKey = key;
    if (this._secureStorage) {
      this._secureStorage.set(STORAGE_KEY, key).catch(() => {
        if (key) localStorage.setItem(STORAGE_KEY, key);
        else localStorage.removeItem(STORAGE_KEY);
      });
    } else {
      if (key) localStorage.setItem(STORAGE_KEY, key);
      else localStorage.removeItem(STORAGE_KEY);
    }
  }

  show() {
    if (this._dialog) this._dialog.remove();

    const apiKey = this._cachedApiKey || '';

    // If no API key, show the setup guide first
    if (!apiKey) {
      this._showSetupGuide();
      return;
    }

    this._showGenerateDialog(apiKey);
  }

  _showSetupGuide() {
    const overlay = el('div', { className: 'dialog-overlay', onClick: (e) => {
      if (e.target === overlay) { overlay.remove(); this._dialog = null; }
    }});
    const dialog = el('div', { className: 'dialog', style: { minWidth: '480px', maxWidth: '520px' } });

    dialog.appendChild(el('h2', { className: 'dialog-title' }, 'AI Tileset Generation'));

    const intro = el('div', { style: { marginBottom: '16px', fontSize: '13px', lineHeight: '1.6', color: 'var(--text)' } });
    intro.innerHTML = `
      <p style="margin-bottom:12px">TileForge can generate pixel art tilesets using <strong>PixelLab AI</strong>. Describe what you want and get a ready-to-use tileset in under 2 minutes.</p>
      <p style="margin-bottom:12px; color:var(--text-dim)">You'll need a free PixelLab account. Here's how:</p>
    `;
    dialog.appendChild(intro);

    const steps = el('div', { style: { marginBottom: '16px' } });
    const stepData = [
      { num: '1', text: 'Go to <a href="https://pixellab.ai" target="_blank" style="color:var(--accent)">pixellab.ai</a> and create a free account' },
      { num: '2', text: 'Open your <a href="https://pixellab.ai/account" target="_blank" style="color:var(--accent)">Account page</a> and find your API key' },
      { num: '3', text: 'Copy the API key and paste it below' },
    ];
    for (const s of stepData) {
      const step = el('div', { style: { display: 'flex', gap: '10px', marginBottom: '8px', fontSize: '13px' } });
      const badge = el('span', { style: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '22px', height: '22px', borderRadius: '50%', flexShrink: '0',
        background: 'var(--accent)', color: '#fff', fontSize: '11px', fontWeight: '600',
      } }, s.num);
      const text = el('span');
      text.innerHTML = s.text;
      step.append(badge, text);
      steps.appendChild(step);
    }
    dialog.appendChild(steps);

    const costNote = el('div', { style: { fontSize: '11px', color: 'var(--text-dim)', marginBottom: '16px', padding: '8px', background: 'var(--bg-input)', borderRadius: '6px' } });
    costNote.textContent = 'Free accounts include a limited number of generations. Paid plans start around $10/month for more. Your key is stored locally in your browser — TileForge never sends it to our servers.';
    dialog.appendChild(costNote);

    // API key input
    const keyGroup = el('div', { className: 'form-group' });
    keyGroup.appendChild(el('label', { className: 'form-label' }, 'PixelLab API Key'));
    const keyInput = el('input', {
      type: 'password',
      className: 'form-input',
      placeholder: 'Paste your API key here',
      style: { fontFamily: 'var(--font-mono)', fontSize: '12px' },
    });
    keyGroup.appendChild(keyInput);
    dialog.appendChild(keyGroup);

    const errorArea = el('div', { style: { fontSize: '12px', color: 'var(--warning)', minHeight: '18px', marginBottom: '8px' } });
    dialog.appendChild(errorArea);

    const btnRow = el('div', { className: 'dialog-buttons' });
    const saveBtn = el('button', { className: 'btn btn-primary', onClick: async () => {
      const key = keyInput.value.trim();
      if (!key) { errorArea.textContent = 'Please paste your API key.'; return; }

      // Validate key by checking balance
      saveBtn.disabled = true;
      saveBtn.textContent = 'Checking...';
      errorArea.textContent = '';

      try {
        const client = new PixelLabClient(key);
        await client.getBalance();
        this._saveApiKey(key);
        overlay.remove();
        this._dialog = null;
        this._showGenerateDialog(key);
      } catch (err) {
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          // CORS issue or network error — save key anyway and try generation later
          this._saveApiKey(key);
          overlay.remove();
          this._dialog = null;
          this._showGenerateDialog(key);
        } else {
          errorArea.textContent = err.message;
        }
      } finally {
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save & Continue';
      }
    }}, 'Save & Continue');
    const cancelBtn = el('button', { className: 'btn', onClick: () => { overlay.remove(); this._dialog = null; } }, 'Cancel');
    btnRow.append(saveBtn, cancelBtn);
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
    this._dialog = overlay;
    keyInput.focus();
  }

  _showGenerateDialog(apiKey) {
    const overlay = el('div', { className: 'dialog-overlay', onClick: (e) => {
      if (e.target === overlay) { overlay.remove(); this._dialog = null; }
    }});
    const dialog = el('div', { className: 'dialog', style: { minWidth: '460px' } });

    dialog.appendChild(el('h2', { className: 'dialog-title' }, 'Generate Tileset (PixelLab AI)'));

    // Type selector
    const typeGroup = el('div', { className: 'form-group' });
    typeGroup.appendChild(el('label', { className: 'form-label' }, 'Generation Type'));
    const typeSelect = el('select', { className: 'form-input' });
    typeSelect.appendChild(el('option', { value: 'topdown' }, 'Top-down Terrain Tileset (Wang tiles)'));
    typeSelect.appendChild(el('option', { value: 'tiles' }, 'Individual Tiles (loose sprites)'));
    typeGroup.appendChild(typeSelect);
    dialog.appendChild(typeGroup);

    // Terrain description fields (for topdown)
    const terrainFields = el('div', { id: 'pixellab-terrain-fields' });

    const lowerGroup = el('div', { className: 'form-group' });
    lowerGroup.appendChild(el('label', { className: 'form-label' }, 'Lower Terrain'));
    const lowerInput = el('input', { type: 'text', className: 'form-input', placeholder: 'e.g. "ocean water", "dirt path", "stone floor"' });
    lowerGroup.appendChild(lowerInput);
    terrainFields.appendChild(lowerGroup);

    const upperGroup = el('div', { className: 'form-group' });
    upperGroup.appendChild(el('label', { className: 'form-label' }, 'Upper Terrain'));
    const upperInput = el('input', { type: 'text', className: 'form-input', placeholder: 'e.g. "sandy beach", "grass", "wooden planks"' });
    upperGroup.appendChild(upperInput);
    terrainFields.appendChild(upperGroup);

    const transGroup = el('div', { className: 'form-group' });
    transGroup.appendChild(el('label', { className: 'form-label' }, 'Transition (optional)'));
    const transInput = el('input', { type: 'text', className: 'form-input', placeholder: 'e.g. "wet sand with foam", "pebbles"' });
    transGroup.appendChild(transInput);
    terrainFields.appendChild(transGroup);

    dialog.appendChild(terrainFields);

    // Individual tiles description (for tiles mode)
    const tilesFields = el('div', { id: 'pixellab-tiles-fields', style: { display: 'none' } });

    const descGroup = el('div', { className: 'form-group' });
    descGroup.appendChild(el('label', { className: 'form-label' }, 'Tile Descriptions'));
    const descInput = el('textarea', {
      className: 'form-input',
      style: { height: '80px', resize: 'vertical', fontFamily: 'var(--font)' },
      placeholder: 'Number each tile:\n1). grass tile\n2). dirt tile\n3). stone tile\n4). water tile',
    });
    descGroup.appendChild(descInput);
    tilesFields.appendChild(descGroup);

    const countGroup = el('div', { className: 'form-group' });
    countGroup.appendChild(el('label', { className: 'form-label' }, 'Number of Tiles'));
    const countSelect = el('select', { className: 'form-input' });
    for (let n = 2; n <= 16; n++) {
      countSelect.appendChild(el('option', { value: String(n) }, `${n} tiles`));
    }
    countSelect.value = '6';
    countGroup.appendChild(countSelect);
    tilesFields.appendChild(countGroup);

    dialog.appendChild(tilesFields);

    // Toggle fields based on type
    typeSelect.addEventListener('change', () => {
      const isTopdown = typeSelect.value === 'topdown';
      terrainFields.style.display = isTopdown ? '' : 'none';
      tilesFields.style.display = isTopdown ? 'none' : '';
    });

    // Tile size
    const sizeGroup = el('div', { className: 'form-group' });
    sizeGroup.appendChild(el('label', { className: 'form-label' }, 'Tile Size'));
    const sizeSelect = el('select', { className: 'form-input' });
    const mapTileSize = this._state.get('tileWidth');
    sizeSelect.appendChild(el('option', { value: '16' }, '16x16'));
    sizeSelect.appendChild(el('option', { value: '32' }, '32x32'));
    sizeSelect.value = mapTileSize <= 16 ? '16' : '32';
    sizeGroup.appendChild(sizeSelect);
    dialog.appendChild(sizeGroup);

    // Progress area
    const progressArea = el('div', { style: { fontSize: '12px', color: 'var(--text-dim)', minHeight: '20px', marginBottom: '4px' } });
    dialog.appendChild(progressArea);

    // Preview area (shows generated image)
    const previewArea = el('div', { style: { textAlign: 'center', marginBottom: '8px', display: 'none' } });
    dialog.appendChild(previewArea);

    // Error area
    const errorArea = el('div', { style: { fontSize: '12px', color: 'var(--warning)', minHeight: '18px', marginBottom: '4px' } });
    dialog.appendChild(errorArea);

    // Buttons
    const btnRow = el('div', { className: 'dialog-buttons' });
    const generateBtn = el('button', { className: 'btn btn-primary', onClick: async () => {
      errorArea.textContent = '';
      progressArea.textContent = '';
      previewArea.style.display = 'none';

      const tileSize = parseInt(sizeSelect.value, 10);
      const client = new PixelLabClient(apiKey);

      generateBtn.disabled = true;
      generateBtn.textContent = 'Generating...';

      try {
        let imageBlob;

        if (typeSelect.value === 'topdown') {
          const lower = lowerInput.value.trim();
          const upper = upperInput.value.trim();
          if (!lower || !upper) { errorArea.textContent = 'Enter both lower and upper terrain descriptions.'; return; }

          const result = await client.createTopdownTileset({
            lowerDesc: lower,
            upperDesc: upper,
            transitionDesc: transInput.value.trim() || null,
            tileSize,
            onProgress: (msg) => { progressArea.textContent = msg; },
          });

          // Download the tileset image
          const imageUrl = result.image_url || result.download_url || result.url;
          if (!imageUrl) throw new Error('No image URL in response. The API may have changed.');
          imageBlob = await client.downloadImage(imageUrl);

        } else {
          const desc = descInput.value.trim();
          if (!desc) { errorArea.textContent = 'Enter tile descriptions.'; return; }

          const result = await client.createTilesPro({
            description: desc,
            tileSize,
            nTiles: parseInt(countSelect.value, 10),
            onProgress: (msg) => { progressArea.textContent = msg; },
          });

          // Tiles Pro returns multiple images — combine into a spritesheet
          const images = result.images || result.tiles || [];
          if (images.length === 0) throw new Error('No tiles generated. Try a different description.');

          imageBlob = await this._combineTilesToSheet(images, tileSize);
        }

        // Show preview
        const previewUrl = URL.createObjectURL(imageBlob);
        previewArea.innerHTML = '';
        const previewImg = el('img', {
          src: previewUrl,
          style: { imageRendering: 'pixelated', maxWidth: '100%', border: '1px solid var(--border)', borderRadius: '4px' },
        });
        previewArea.appendChild(previewImg);
        previewArea.style.display = '';

        // Load into editor
        await this._loadIntoEditor(imageBlob, typeSelect.value === 'topdown' ? `${lowerInput.value}-${upperInput.value}` : 'PixelLab Tiles', tileSize);

        progressArea.textContent = 'Tileset generated and loaded into editor!';
        progressArea.style.color = 'var(--success)';

      } catch (err) {
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError') || err.message.includes('CORS')) {
          errorArea.innerHTML = 'Browser blocked the request (CORS). PixelLab may not support direct browser calls yet. ' +
            '<br>Try using the <a href="https://pixellab.ai" target="_blank" style="color:var(--accent)">PixelLab web app</a> to generate, then import the PNG via File > Add Tileset.';
        } else {
          errorArea.textContent = err.message;
        }
      } finally {
        generateBtn.disabled = false;
        generateBtn.textContent = 'Generate';
      }
    }}, 'Generate');

    const settingsBtn = el('button', { className: 'btn', onClick: () => {
      overlay.remove();
      this._dialog = null;
      this._saveApiKey(null);
      this._showSetupGuide();
    }}, 'Change API Key');

    const cancelBtn = el('button', { className: 'btn', onClick: () => { overlay.remove(); this._dialog = null; } }, 'Close');
    btnRow.append(generateBtn, settingsBtn, cancelBtn);
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
    this._dialog = overlay;
  }

  /**
   * Combine individual tile images (base64 PNGs) into a single spritesheet.
   */
  async _combineTilesToSheet(images, tileSize) {
    const canvas = document.createElement('canvas');
    const cols = Math.min(images.length, 8);
    const rows = Math.ceil(images.length / cols);
    canvas.width = cols * tileSize;
    canvas.height = rows * tileSize;
    const ctx = canvas.getContext('2d');

    for (let i = 0; i < images.length; i++) {
      const imgData = images[i].base64 || images[i].image || images[i];
      const src = typeof imgData === 'string' && !imgData.startsWith('data:')
        ? `data:image/png;base64,${imgData}`
        : imgData;

      const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Failed to load tile image'));
        image.src = typeof src === 'string' ? src : URL.createObjectURL(src);
      });

      const col = i % cols;
      const row = Math.floor(i / cols);
      ctx.drawImage(img, col * tileSize, row * tileSize, tileSize, tileSize);
    }

    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }

  /**
   * Load a generated tileset image into the editor.
   */
  async _loadIntoEditor(blob, name, tileSize) {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      const url = URL.createObjectURL(blob);
      image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
      image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load tileset image')); };
      image.src = url;
    });

    // Store a persistent URL for the tilesetImages map
    const persistUrl = URL.createObjectURL(blob);
    const tilesetLoader = window.__tileforge?.tilesetLoader;
    if (!tilesetLoader) throw new Error('Editor not ready');

    const safeName = name.replace(/[^a-zA-Z0-9 _-]/g, '').slice(0, 40) || 'PixelLab';
    tilesetLoader._createTilesetFromImage(img, safeName, persistUrl);

    this._mapCanvas.invalidateAll();
    this._compositor.requestRender();
  }
}
